import ExcelJS from "exceljs";
import { programRank } from "../CampAttendanceReport/campAttendanceSort";

const HEADER_FILL = "FFDCE6F1"; // light blue header (matches the attendance sheet)
const HEADER_FONT = "FF1F3864"; // dark navy header text
const BORDER_COLOR = "FF000000"; // thin black border on every cell
const TEXT_COLOR = "FF333333";
const FONT_NAME = "Calibri";

const WEEKS_COL_WIDTH = 38;
const COUNT_COL_WIDTH = 10;
const SPACER_COL_WIDTH = 2;

const allBorders = {
  top: { style: "thin", color: { argb: BORDER_COLOR } },
  left: { style: "thin", color: { argb: BORDER_COLOR } },
  bottom: { style: "thin", color: { argb: BORDER_COLOR } },
  right: { style: "thin", color: { argb: BORDER_COLOR } },
};

function styleHeaderCell(cell) {
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  cell.font = { name: FONT_NAME, bold: true, size: 11, color: { argb: HEADER_FONT } };
  cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
  cell.border = allBorders;
}

// A camp type counts toward AM unless it is an afternoon-only type (HD-PM*),
// and toward PM unless it is a morning-only type (HD-AM). Full-day types
// (FD, FD-EX) count toward both.
const countsTowardAM = (campType) => !campType.startsWith("HD-PM");
const countsTowardPM = (campType) => !campType.startsWith("HD-AM");

const weekStartDate = (campWeek) => {
  // camp_week arrives as "Aug 3, 2026 - Aug 7, 2026" (start - end). Older shapes
  // used "2026-06-01 / 2026-06-05"; accept either separator and parse the start.
  const start = String(campWeek).split(/\s+[-/]\s+/)[0];
  const parsed = new Date(start);
  return isNaN(parsed) ? null : parsed;
};

const sumCounts = (byType, campTypes) =>
  campTypes.reduce((sum, ct) => sum + (byType[ct] || 0), 0);

const amPm = (byType, campTypes) => ({
  am: campTypes.filter(countsTowardAM).reduce((s, ct) => s + (byType[ct] || 0), 0),
  pm: campTypes.filter(countsTowardPM).reduce((s, ct) => s + (byType[ct] || 0), 0),
});

/**
 * Pivots the camp Registered Student List rows into:
 * week -> stream -> count per camp type, with week subtotals,
 * AM/PM presence counts and an overall grand total.
 */
export const buildCampSummary = (reports) => {
  // Dynamic columns: the distinct camp types present in the results.
  const campTypes = [
    ...new Set((reports || []).map((r) => r.camp_type).filter(Boolean)),
  ].sort();

  // Streams are keyed by course code so distinct courses never merge, even when
  // their abbreviations collide. The program name (`program_type` from the camp
  // endpoint) is carried through so streams can be ordered Robotics-first, like
  // the Camp Attendance report.
  const weekMap = new Map();
  (reports || []).forEach((r) => {
    if (!r.camp_week || !r.camp_type) return;
    if (!weekMap.has(r.camp_week)) weekMap.set(r.camp_week, new Map());
    const streamMap = weekMap.get(r.camp_week);
    const abbr = r.course_abbr || "(no stream)";
    const key = r.course_code || abbr;
    if (!streamMap.has(key)) {
      streamMap.set(key, {
        abbr,
        code: r.course_code || "",
        program: r.program_type || "",
        byType: {},
      });
    }
    const stream = streamMap.get(key);
    stream.byType[r.camp_type] = (stream.byType[r.camp_type] || 0) + 1;
  });

  const naturalCompare = (a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true });

  // Program rank (Robotics -> Coding -> Technology -> others), then course code,
  // then abbreviation — mirrors the Camp Attendance row ordering.
  const compareStreams = (a, b) =>
    programRank(a.program) - programRank(b.program) ||
    naturalCompare(a.code, b.code) ||
    naturalCompare(a.abbr, b.abbr);

  const weeks = [...weekMap.keys()]
    .sort((a, b) => {
      const dateA = weekStartDate(a);
      const dateB = weekStartDate(b);
      return dateA && dateB ? dateA - dateB : naturalCompare(a, b);
    })
    .map((week) => {
      const streamMap = weekMap.get(week);
      const streams = [...streamMap.values()].sort(compareStreams).map((s) => {
        const { byType } = s;
        return {
          abbr: s.abbr,
          code: s.code,
          program: s.program,
          byType,
          total: sumCounts(byType, campTypes),
          ...amPm(byType, campTypes),
        };
      });
      const byType = {};
      streams.forEach((s) =>
        campTypes.forEach((ct) => {
          if (s.byType[ct]) byType[ct] = (byType[ct] || 0) + s.byType[ct];
        })
      );
      return {
        week,
        streams,
        byType,
        total: sumCounts(byType, campTypes),
        ...amPm(byType, campTypes),
      };
    });

  const grandByType = {};
  weeks.forEach((w) =>
    campTypes.forEach((ct) => {
      if (w.byType[ct]) grandByType[ct] = (grandByType[ct] || 0) + w.byType[ct];
    })
  );
  const grand = {
    byType: grandByType,
    total: sumCounts(grandByType, campTypes),
    ...amPm(grandByType, campTypes),
  };

  return { campTypes, weeks, grand };
};

export const generateCampSummaryWorkbook = async (reports) => {
  const { campTypes, weeks, grand } = buildCampSummary(reports);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Summary", {
    views: [{ state: "frozen", ySplit: 2 }], // keep the header visible while scrolling
  });

  // Columns: Weeks | <camp types...> | Grand Total | spacer | AM | PM
  const firstTypeCol = 2;
  const grandTotalCol = firstTypeCol + campTypes.length;
  const spacerCol = grandTotalCol + 1;
  const amCol = spacerCol + 1;
  const pmCol = amCol + 1;

  worksheet.getColumn(1).width = WEEKS_COL_WIDTH;
  for (let c = firstTypeCol; c <= grandTotalCol; c++) {
    worksheet.getColumn(c).width = COUNT_COL_WIDTH;
  }
  worksheet.getColumn(spacerCol).width = SPACER_COL_WIDTH;
  worksheet.getColumn(amCol).width = COUNT_COL_WIDTH;
  worksheet.getColumn(pmCol).width = COUNT_COL_WIDTH;

  // ---- Header (rows 1-2) ----
  worksheet.mergeCells(1, 1, 2, 1);
  worksheet.getCell(1, 1).value = "Weeks";

  worksheet.mergeCells(1, firstTypeCol, 1, grandTotalCol);
  worksheet.getCell(1, firstTypeCol).value = "Camp Type";
  campTypes.forEach((ct, i) => {
    worksheet.getCell(2, firstTypeCol + i).value = ct;
  });
  worksheet.getCell(2, grandTotalCol).value = "Grand Total";

  worksheet.mergeCells(1, amCol, 1, pmCol);
  worksheet.getCell(1, amCol).value = "Summary Student Count";
  worksheet.getCell(2, amCol).value = "AM";
  worksheet.getCell(2, pmCol).value = "PM";

  for (let r = 1; r <= 2; r++) {
    for (let c = 1; c <= pmCol; c++) {
      if (c === spacerCol) continue; // spacer stays unstyled
      styleHeaderCell(worksheet.getCell(r, c));
    }
  }
  worksheet.getRow(1).height = 24;
  worksheet.getRow(2).height = 18;

  // ---- Data rows ----
  // Zero counts stay blank, like a pivot table.
  const writeCounts = (wsRow, byType, total, am, pm, bold) => {
    const font = { name: FONT_NAME, size: 11, bold, color: { argb: TEXT_COLOR } };
    campTypes.forEach((ct, i) => {
      const cell = wsRow.getCell(firstTypeCol + i);
      cell.value = byType[ct] || null;
      cell.font = font;
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    [
      [grandTotalCol, total],
      [amCol, am],
      [pmCol, pm],
    ].forEach(([col, value]) => {
      const cell = wsRow.getCell(col);
      cell.value = value;
      cell.font = font;
      cell.alignment = { vertical: "middle", horizontal: "center" };
    });
    for (let c = 1; c <= pmCol; c++) {
      if (c === spacerCol) continue;
      wsRow.getCell(c).border = allBorders;
    }
  };

  let excelRow = 3;
  weeks.forEach((week) => {
    const weekRow = worksheet.getRow(excelRow);
    weekRow.height = 20;
    const weekCell = weekRow.getCell(1);
    weekCell.value = week.week;
    weekCell.font = { name: FONT_NAME, size: 11, bold: true, color: { argb: TEXT_COLOR } };
    weekCell.alignment = { vertical: "middle", horizontal: "left" };
    writeCounts(weekRow, week.byType, week.total, week.am, week.pm, true);
    excelRow += 1;

    week.streams.forEach((stream) => {
      const streamRow = worksheet.getRow(excelRow);
      streamRow.height = 18;
      const streamCell = streamRow.getCell(1);
      streamCell.value = stream.abbr;
      streamCell.font = { name: FONT_NAME, size: 11, color: { argb: TEXT_COLOR } };
      streamCell.alignment = { vertical: "middle", horizontal: "left", indent: 2 };
      writeCounts(streamRow, stream.byType, stream.total, stream.am, stream.pm, false);
      excelRow += 1;
    });
  });

  // Grand total row, banded like the header.
  const totalRow = worksheet.getRow(excelRow);
  totalRow.height = 20;
  totalRow.getCell(1).value = "Grand Total";
  writeCounts(totalRow, grand.byType, grand.total, grand.am, grand.pm, true);
  for (let c = 1; c <= pmCol; c++) {
    if (c === spacerCol) continue;
    styleHeaderCell(totalRow.getCell(c)); // restyles, keeps values
  }
  totalRow.getCell(1).alignment = { vertical: "middle", horizontal: "left" };

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadCampSummarySheet = async (reports, branchName) => {
  const blob = await generateCampSummaryWorkbook(reports);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `${branchName} - Registered Student List - Summary.xlsx`
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
